import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useBalance, useTransactions, useTopup } from '../hooks/useCredits'
import { Wallet, Plus, Loader2 } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

const TOPUP_OPTIONS = [
  { cents: 2000, label: '$20' },
  { cents: 5000, label: '$50' },
  { cents: 10000, label: '$100' },
  { cents: 20000, label: '$200' },
]

const typeColour: Record<string, string> = {
  topup: 'bg-green-500/10 text-green-600',
  order_charge: 'bg-blue-500/10 text-blue-600',
  refund: 'bg-yellow-500/10 text-yellow-600',
  adjustment: 'bg-purple-500/10 text-purple-600',
}

export function AccountPage() {
  const [searchParams] = useSearchParams()
  const topupStatus = searchParams.get('topup')
  const { data: balanceData, isLoading: balanceLoading } = useBalance()
  const { data: txData, isLoading: txLoading } = useTransactions()
  const topup = useTopup()

  const handleTopup = (amountCents: number) => {
    topup.mutate(amountCents, {
      onSuccess: (data) => {
        // Redirect to Stripe Checkout
        window.location.href = (data as any).checkout_url
      },
    })
  }

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Account</h1>
        <p className="text-muted-foreground">Manage your balance and view transaction history.</p>
      </div>

      {topupStatus === 'success' && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200">
          Top-up successful! Your balance has been updated.
        </div>
      )}

      {topupStatus === 'cancelled' && (
        <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800 dark:border-yellow-900 dark:bg-yellow-950 dark:text-yellow-200">
          Top-up was cancelled.
        </div>
      )}

      {/* Balance Card */}
      <Card className="mb-8">
        <CardContent className="flex items-center justify-between pt-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Wallet className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Current Balance</p>
              {balanceLoading ? (
                <Skeleton className="h-9 w-28" />
              ) : (
                <p className="text-3xl font-bold font-mono">
                  {balanceData?.balance ?? '$0.00'}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Up Options */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Top Up Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {TOPUP_OPTIONS.map((opt) => (
              <Button
                key={opt.cents}
                variant="outline"
                size="lg"
                className="h-16 text-lg font-mono hover:border-primary hover:shadow-sm transition-all"
                disabled={topup.isPending}
                onClick={() => handleTopup(opt.cents)}
              >
                {topup.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  opt.label
                )}
              </Button>
            ))}
          </div>
          {topup.isError && (
            <p className="mt-3 text-sm text-destructive">{topup.error.message}</p>
          )}
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          {txLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !txData?.transactions?.length ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No transactions yet. Top up your balance to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txData.transactions.map((tx) => {
                  const isPositive = tx.amount_cents > 0
                  return (
                    <TableRow key={tx.id}>
                      <TableCell className="text-muted-foreground">
                        {new Date(tx.created_at).toLocaleDateString('en-AU')}
                      </TableCell>
                      <TableCell>{tx.description}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={typeColour[tx.type] ?? ''}>
                          {tx.type}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-right font-mono ${isPositive ? 'text-green-600' : ''}`}>
                        {isPositive ? '+' : ''}{tx.amount}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {tx.balance_after}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
